"use client"

import { useId, useMemo } from 'react'
import {
  LOSS_OF_CONTROL_BOUNDARY_LINE_COLOR,
  LOSS_OF_CONTROL_BOUNDARY_LINE_OPACITY,
  LOSS_OF_CONTROL_BOUNDARY_LINE_STROKE_WIDTH,
} from './graphConstants'
import { useBackgroundPatternSettings } from './BackgroundPatternSettingsProvider'
import type { GraphPoint } from './types'

const SUBDIVISIONS_PER_SEGMENT = 24

export const LossOfControlGradientStrip = ({
  show,
  lossOfControlMinPoints,
  lossOfControlMaxPoints,
  effectiveCappedTimestamp,
  xForTimestamp,
  yForValue,
  showTopDashedLine = false,
  showBottomDashedLine = false,
  showGradientStrip = true,
  lineColor = LOSS_OF_CONTROL_BOUNDARY_LINE_COLOR,
  transitionDurationMs = 0,
}: {
  show: boolean
  lossOfControlMinPoints: GraphPoint[]
  lossOfControlMaxPoints: GraphPoint[]
  effectiveCappedTimestamp: number
  xForTimestamp: (timestamp: number) => number
  yForValue: (value: number) => number
  showTopDashedLine?: boolean
  showBottomDashedLine?: boolean
  showGradientStrip?: boolean
  lineColor?: string
  transitionDurationMs?: number
}) => {
  const gradientIdPrefix = useId().replace(/:/g, '-')
  const {
    lossOfControlGradientStripOpacity,
    lossOfControlLineYOffset,
  } = useBackgroundPatternSettings()
  const lineStyle = {
    stroke: lineColor,
    strokeOpacity: LOSS_OF_CONTROL_BOUNDARY_LINE_OPACITY,
    transition: `stroke ${transitionDurationMs}ms ease`,
  }
  const shouldRenderGradientStrip =
    showGradientStrip && lossOfControlGradientStripOpacity > 0.001

  const visibleBoundaryPoints = useMemo(
    () => {
      if (!show) {
        return { lower: [] as GraphPoint[], upper: [] as GraphPoint[] }
      }
      const visibleLossOfControlMinPoints = lossOfControlMinPoints.filter(graphPoint =>
        graphPoint.timestamp <= effectiveCappedTimestamp
      )
      const visibleLossOfControlMaxPoints = lossOfControlMaxPoints.filter(
        graphPoint => graphPoint.timestamp <= effectiveCappedTimestamp
      )
      const alignedPointCount = Math.min(
        visibleLossOfControlMinPoints.length,
        visibleLossOfControlMaxPoints.length,
      )
      return {
        lower: visibleLossOfControlMinPoints.slice(0, alignedPointCount),
        upper: visibleLossOfControlMaxPoints.slice(0, alignedPointCount),
      }
    },
    [show, lossOfControlMinPoints, lossOfControlMaxPoints, effectiveCappedTimestamp],
  )

  const fillBands = useMemo(
    () => {
      if (!shouldRenderGradientStrip) return []
      const visibleLossOfControlMinPoints = visibleBoundaryPoints.lower
      const visibleLossOfControlMaxPoints = visibleBoundaryPoints.upper
      const alignedPointCount = visibleLossOfControlMinPoints.length
      if (alignedPointCount < 2) return []
      const lossOfControlSegmentIndices = Array.from(
        { length: alignedPointCount - 1 },
        (_, index) => index
      )
      return lossOfControlSegmentIndices.flatMap(segmentIndex => {
        const lowerStartPoint = visibleLossOfControlMinPoints[segmentIndex]
        const lowerEndPoint = visibleLossOfControlMinPoints[segmentIndex + 1]
        const upperStartPoint = visibleLossOfControlMaxPoints[segmentIndex]
        const upperEndPoint = visibleLossOfControlMaxPoints[segmentIndex + 1]
        const lossOfControlSubsegmentIndices = Array.from(
          { length: SUBDIVISIONS_PER_SEGMENT },
          (_, index) => index
        )
        return lossOfControlSubsegmentIndices.map(subsegmentIndex => {
          const subsegmentStartProgress = subsegmentIndex / SUBDIVISIONS_PER_SEGMENT
          const subsegmentEndProgress = (subsegmentIndex + 1) / SUBDIVISIONS_PER_SEGMENT
          const lowerStartX = xForTimestamp(
            lowerStartPoint.timestamp +
            ((lowerEndPoint.timestamp - lowerStartPoint.timestamp) * subsegmentStartProgress)
          )
          const lowerStartY = yForValue(
            lowerStartPoint.value +
            ((lowerEndPoint.value - lowerStartPoint.value) * subsegmentStartProgress)
          )
          const lowerEndX = xForTimestamp(
            lowerStartPoint.timestamp +
            ((lowerEndPoint.timestamp - lowerStartPoint.timestamp) * subsegmentEndProgress)
          )
          const lowerEndY = yForValue(
            lowerStartPoint.value +
            ((lowerEndPoint.value - lowerStartPoint.value) * subsegmentEndProgress)
          )
          const upperStartX = xForTimestamp(
            upperStartPoint.timestamp +
            ((upperEndPoint.timestamp - upperStartPoint.timestamp) * subsegmentStartProgress)
          )
          const upperStartY = yForValue(
            upperStartPoint.value +
            ((upperEndPoint.value - upperStartPoint.value) * subsegmentStartProgress)
          )
          const upperEndX = xForTimestamp(
            upperStartPoint.timestamp +
            ((upperEndPoint.timestamp - upperStartPoint.timestamp) * subsegmentEndProgress)
          )
          const upperEndY = yForValue(
            upperStartPoint.value +
            ((upperEndPoint.value - upperStartPoint.value) * subsegmentEndProgress)
          )
          const lowerMidX = (lowerStartX + lowerEndX) / 2
          const lowerMidY = (lowerStartY + lowerEndY) / 2
          const upperMidX = (upperStartX + upperEndX) / 2
          const upperMidY = (upperStartY + upperEndY) / 2
          return {
            key: `projected-loss-of-control-band-${segmentIndex}-${subsegmentIndex}`,
            d: `M ${lowerStartX} ${lowerStartY} L ${lowerEndX} ${lowerEndY} L ${upperEndX} ${upperEndY} L ${upperStartX} ${upperStartY} Z`,
            gradientId: `${gradientIdPrefix}-${segmentIndex}-${subsegmentIndex}`,
            x1: lowerMidX,
            y1: lowerMidY,
            x2: upperMidX,
            y2: upperMidY,
          }
        })
      })
    },
    [
      shouldRenderGradientStrip,
      visibleBoundaryPoints,
      xForTimestamp,
      yForValue,
      gradientIdPrefix,
    ],
  )

  const buildBoundaryPathData = (boundaryPoints: GraphPoint[], yOffset = 0) => boundaryPoints
    .map((boundaryPoint, boundaryPointIndex) => {
      const x = xForTimestamp(boundaryPoint.timestamp)
      const y = yForValue(boundaryPoint.value) + yOffset
      return `${boundaryPointIndex === 0 ? 'M' : 'L'} ${x} ${y}`
    })
    .join(' ')

  const topDashedPathData = showTopDashedLine && visibleBoundaryPoints.lower.length >= 2
    ? buildBoundaryPathData(visibleBoundaryPoints.lower, lossOfControlLineYOffset)
    : null
  const bottomDashedPathData = showBottomDashedLine && visibleBoundaryPoints.upper.length >= 2
    ? buildBoundaryPathData(visibleBoundaryPoints.upper)
    : null

  if (fillBands.length === 0 && !topDashedPathData && !bottomDashedPathData) return null

  return (
    <>
      <defs>
        {fillBands.map(fillBand => (
          <linearGradient
            key={fillBand.gradientId}
            id={fillBand.gradientId}
            gradientUnits="userSpaceOnUse"
            x1={fillBand.x1}
            y1={fillBand.y1}
            x2={fillBand.x2}
            y2={fillBand.y2}
          >
            <stop offset="0%" stopColor="var(--race-background)" stopOpacity={0} />
            <stop
              offset="100%"
              stopColor="var(--race-background)"
              stopOpacity={lossOfControlGradientStripOpacity}
            />
          </linearGradient>
        ))}
      </defs>
      {fillBands.map(fillBand => (
        <path
          key={fillBand.key}
          d={fillBand.d}
          fill={`url(#${fillBand.gradientId})`}
        />
      ))}
      {topDashedPathData && (
        <path
          d={topDashedPathData}
          fill="none"
          strokeWidth={LOSS_OF_CONTROL_BOUNDARY_LINE_STROKE_WIDTH}
          strokeDasharray="2 4"
          style={lineStyle}
        />
      )}
      {bottomDashedPathData && (
        <path
          d={bottomDashedPathData}
          fill="none"
          strokeWidth={LOSS_OF_CONTROL_BOUNDARY_LINE_STROKE_WIDTH}
          strokeDasharray="4 4"
          style={lineStyle}
        />
      )}
    </>
  )
}
